import { useEffect, useRef, useState } from 'react';
import { TextField } from '@mui/material';
import { AdviseSupplierContract } from '../../../../types/advise';
import { contractFieldError } from './currentSupplierLimits';
import { AutoRenewField } from './AutoRenewField';

export interface ContractFieldsProps {
  contract: AdviseSupplierContract;
  canEdit: boolean;
  onSave: (change: AdviseSupplierContract) => void;
}

type TypedField = 'end_date' | 'term_months' | 'early_termination_fee';
type Draft = Record<TypedField, string>;
const TYPED_FIELDS: TypedField[] = ['end_date', 'term_months', 'early_termination_fee'];
const LIMITS: Partial<Record<TypedField, { min: number; step?: number }>> = {
  term_months: { min: 0, step: 1 },
  early_termination_fee: { min: 0 }
};

const draftOf = (contract: AdviseSupplierContract): Draft => ({
  end_date: contract.end_date ?? '',
  term_months: contract.term_months?.toString() ?? '',
  early_termination_fee: contract.early_termination_fee?.toString() ?? ''
});

const parsed = (field: TypedField, text: string) => {
  if (text.trim() === '') return null;
  return field === 'end_date' ? text : Number(text);
};

// Takes a field's newly saved value only when that value changed, and never into a field typed in since it was
// focused, so a save landing for one field can't wipe what is half-typed in another.
const useContractDrafts = (contract: AdviseSupplierContract) => {
  const [draft, setDraft] = useState(() => draftOf(contract));
  const synced = useRef(draft);
  const typedIn = useRef<TypedField | null>(null);
  const [errors, setErrors] = useState<Partial<Record<TypedField, string | null>>>({});
  const { end_date, term_months, early_termination_fee } = contract;

  useEffect(() => {
    const saved = draftOf({ end_date, term_months, early_termination_fee });
    const landed = TYPED_FIELDS.filter((field) => field !== typedIn.current && saved[field] !== synced.current[field]);
    synced.current = saved;
    if (landed.length === 0) return;
    setDraft((current) => ({ ...current, ...Object.fromEntries(landed.map((field) => [field, saved[field]])) }));
    setErrors((current) => ({ ...current, ...Object.fromEntries(landed.map((field) => [field, null])) }));
  }, [end_date, term_months, early_termination_fee]);

  return { draft, setDraft, typedIn, errors, setErrors };
};

// A typed field saves when it is left changed, and one the API would refuse stays in error instead of being sent.
const useTypedFields = ({ contract, canEdit, onSave }: ContractFieldsProps) => {
  const { draft, setDraft, typedIn, errors, setErrors } = useContractDrafts(contract);

  const leave = (field: TypedField, unreadable: boolean) => {
    if (typedIn.current !== field) return;
    typedIn.current = null;
    const error = contractFieldError(field, draft[field], unreadable);
    setErrors({ ...errors, [field]: error });
    if (error) return;
    const value = parsed(field, draft[field]);
    if (value !== (contract[field] ?? null)) onSave({ [field]: value });
  };

  return (field: TypedField, label: string) => ({
    id: `contract-${field}`,
    label,
    size: 'small' as const,
    value: draft[field],
    InputLabelProps: { shrink: true },
    InputProps: { readOnly: !canEdit },
    inputProps: LIMITS[field],
    error: !!errors[field],
    helperText: errors[field],
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
      typedIn.current = field;
      setDraft({ ...draft, [field]: event.target.value });
      const unreadable = !!event.target.validity?.badInput;
      if (errors[field]) setErrors({ ...errors, [field]: contractFieldError(field, event.target.value, unreadable) });
    },
    onFocus: () => {
      typedIn.current = null;
    },
    onBlur: (event: React.FocusEvent<HTMLInputElement>) => leave(field, !!event.target.validity?.badInput)
  });
};

/** The four optional contract fields; auto-renew saves as soon as it is picked. */
export const ContractFields = (props: ContractFieldsProps) => {
  const { contract, canEdit, onSave } = props;
  const fieldProps = useTypedFields(props);

  return (
    <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
      <TextField
        type="date"
        {...fieldProps('end_date', 'Contract end date')}
      />
      <TextField
        type="number"
        {...fieldProps('term_months', 'Term (months)')}
      />
      <AutoRenewField
        autoRenew={contract.auto_renew}
        canEdit={canEdit}
        onPick={(autoRenew) => onSave({ auto_renew: autoRenew })}
      />
      <TextField
        type="number"
        {...fieldProps('early_termination_fee', 'Early termination fee')}
      />
    </div>
  );
};
